import { createAsyncThunk } from '@reduxjs/toolkit';
import { fetchApiAuth } from 'core/utils/api';
import { handleError } from 'core/utils/helpers';
import { notification } from 'antd';
import { Notification, NotificationResponse, NotificationUpdateResponse } from './types';
import parseContentDispositionFilename from './downloadHelpers';

export const fetchNotifications = createAsyncThunk(
    'notification/fetch',
    async (_, { rejectWithValue }) => {
        try {
            const response: NotificationResponse = await fetchApiAuth({
                method: 'GET',
                url: 'notifications',
            });

            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Notification Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const fetchUnreadNotifications = createAsyncThunk(
    'notification/unread',
    async (_, { rejectWithValue }) => {
        try {
            const response: NotificationResponse = await fetchApiAuth({
                method: 'GET',
                url: 'notifications/unread',
                background: true, // polled by NotificationPoller; must not count as user activity
            });

            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Notification Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const markNotificationAsReadServer = createAsyncThunk(
    'notification/read',
    async (id: string, { rejectWithValue }) => {
        try {
            const response: NotificationUpdateResponse = await fetchApiAuth({
                method: 'PUT',
                url: 'notifications/read',
                body: { id },
            });

            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Notification Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const markAllNotificationsAsReadServer = createAsyncThunk(
    'notification/readAll',
    async (_, { rejectWithValue }) => {
        try {
            const response: NotificationUpdateResponse = await fetchApiAuth({
                method: 'POST',
                url: 'notifications/read-all',
            });

            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Notification Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const downloadNotificationFile = createAsyncThunk(
    'notification/download',
    async (notif: Notification, { rejectWithValue }) => {
        try {
            if (!notif.actionUrl) {
                return undefined;
            }

            // The actionUrl is the full, environment-correct API URL emitted by
            // the backend; pass it through unchanged so fetchApiAuth fetches it
            // with the bearer token attached (axios honours the absolute URL).
            const response = await fetchApiAuth({
                method: 'GET',
                url: notif.actionUrl,
                responseType: 'blob',
            });

            const disposition = (response.headers as Record<string, string>)?.['content-disposition'];
            const filename = parseContentDispositionFilename(disposition, 'incident-export.csv');

            const blobUrl = window.URL.createObjectURL(response.data as Blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);

            return undefined;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Download Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const fetchRecentNotifications = createAsyncThunk(
    'notification/recent',
    async (_, { rejectWithValue }) => {
        try {
            const response: NotificationResponse = await fetchApiAuth({
                method: 'GET',
                url: 'notifications/recent',
                background: true, // polled every 30s by NotificationPoller; must not count as user activity
            });

            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                notification.error({
                    message: 'Notification Error',
                    description: errorMessage,
                });
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);
